import { MUITab } from "@abb/arcadia-ui-react"
import { useContext, useEffect, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { digitalManagerApi } from "../../../../../services/api"
import {
  DiMaDetailsHeader,
  DiMaDetailsHeaderProps,
} from "../../../../components/DiMaDetailsHeader"
import { DiMaProgress } from "../../../../components/DiMaProgress"
import { UserContext } from "../../../../context/UserContext"
import { ApplicationRoleGet } from "../../../../models/ApplicationRole"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  getAppFeatureAccessLevelByAppId,
} from "../../../../models/Authorization"
import { useHashTab } from "../../../../../hooks/useHashTab"
import { ApplicationRoleApprovers } from "./approvers_tab/ApplicationRoleApprovers"
import { ApplicationRoleDetails } from "./details_tab/ApplicationRoleDetails"
import { ApplicationRolePermissions } from "./permissions_tab/ApplicationRolePermissions"
import { ApplicationRoleUsers } from "./users_tab/ApplicationRoleUsers"

export function ApplicationRoleContainer() {
  const { user, isLoadingUser } = useContext(UserContext)
  const { applicationId, roleId } = useParams()
  const navigate = useNavigate()

  const { activeTab, handleTabChange: handleChange } = useHashTab("details")

  const [role, setRole] = useState<ApplicationRoleGet>()

  const returnURL = `/applications/${applicationId}#roles`

  useEffect(() => {
    if (applicationId && roleId) {
      digitalManagerApi
        .get<ApplicationRoleGet>(`/applications/${applicationId}/roles/${roleId}`)
        .then((res) => {
          setRole(res.data)
        })
        .catch((err) => {
          if (err.response.status) {
            navigate(returnURL)
          }
        })
    }
  }, [applicationId, navigate, returnURL, roleId])

  if (!applicationId || !roleId) {
    return <Navigate to="/applications" />
  }

  if (!role || isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const detailsHeaderProps: DiMaDetailsHeaderProps = {
    url: returnURL,
    title: role.name,
    label: "Role ID",
    subtitle: role.id,
  }

  const canUserAccessRoleUsers =
    user.authorizations &&
    getAppFeatureAccessLevelByAppId(
      applicationId,
      DimaFeatureShortNameEnum.RM,
      user.authorizations.applications
    ) >= AccessLevel.Read

  const canUserAccessRoleApprovers =
    user.authorizations &&
    getAppFeatureAccessLevelByAppId(
      applicationId,
      DimaFeatureShortNameEnum.AM,
      user.authorizations.applications
    ) >= AccessLevel.Read

  return (
    <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div style={{ flex: 10 }}>
          <DiMaDetailsHeader {...detailsHeaderProps} />
        </div>
      </div>

      <MUITab
        tabItems={[
          { id: "details", label: "Details" },
          { id: "permissions", label: "Permissions" },
          { id: "users", label: "Users", isDisabled: !canUserAccessRoleUsers },
          {
            id: "approvers",
            label: "Approvers",
            isDisabled: !canUserAccessRoleApprovers,
          },
        ]}
        value={activeTab}
        handleChange={handleChange}
      />

      {activeTab === "details" && (
        <ApplicationRoleDetails
          role={role}
          applicationId={applicationId}
          user={user}
          setRole={setRole}
        />
      )}

      {activeTab === "permissions" && <ApplicationRolePermissions />}

      {canUserAccessRoleUsers && activeTab === "users" && <ApplicationRoleUsers />}

      {canUserAccessRoleApprovers && activeTab === "approvers" && (
        <ApplicationRoleApprovers />
      )}
    </div>
  )
}
